// Directions from hitta (served under /api on the same host). Reach, the default,
// draws how far one gets from a point within a time budget (an isochrone). Route
// goes from a start to a destination. A long press on the map (a right click with a
// mouse) opens a menu that sets either point. The markers can be dragged.
import * as maplibregl from "maplibre-gl";

const MODES = [
  ["car", "Car"],
  ["bicycle", "Bike"],
  ["foot", "Walk"],
  ["transit", "Transit"],
];

const MINUTES = [5, 10, 15, 30, 45, 60, 90, 120, 180];

const MARKERS = { start: "#16a34a", end: "#dc2626" };

const HINT = "Long-press or right-click the map";

const COLORS = { route: "#2563eb", walk: "#64748b", ride: "#dc2626" };

export function directions(map) {
  const panel = document.createElement("div");
  panel.className = "directions";
  panel.innerHTML = `
    <div class="modes kinds"><button data-kind="reach">Reach</button><button data-kind="route">Route</button></div>
    <div class="modes">${MODES.map(([m, label]) => `<button data-mode="${m}">${label}</button>`).join("")}</div>
    <label class="minutes" hidden>within <select>${MINUTES.map((m) => `<option value="${m}"${m === 60 ? " selected" : ""}>${duration(m * 60)}</option>`).join("")}</select></label>
    <label class="depart" hidden>leaving <select class="day"></select> <select class="time" hidden></select></label>
    <div class="status"></div>
    <ol class="journeys"></ol>
    <button class="clear" hidden>Clear</button>`;
  document.body.append(panel);
  const status = panel.querySelector(".status");
  const journeys = panel.querySelector(".journeys");
  const clear = panel.querySelector(".clear");
  const minutes = panel.querySelector(".minutes");
  minutes.querySelector("select").onchange = () => update();
  // Departure, for transit: now (taken when the request goes out), or a day this
  // week at a quarter hour. The days are rebuilt on open, in case midnight passed.
  const depart = panel.querySelector(".depart");
  const day = depart.querySelector(".day");
  const time = depart.querySelector(".time");
  const days = () => {
    const today = stockholmNow().slice(0, 10);
    const chosen = day.value;
    day.innerHTML = `<option value="">now</option>${[...Array(7).keys()]
      .map((i) => {
        const date = addDays(today, i);
        return `<option value="${date}">${i === 0 ? "today" : i === 1 ? "tomorrow" : weekday(date)}</option>`;
      })
      .join("")}`;
    day.value = chosen >= today ? chosen : "";
  };
  days();
  day.onfocus = days;
  time.innerHTML = [...Array(96).keys()]
    .map((q) => {
      const hhmm = `${String(Math.floor(q / 4)).padStart(2, "0")}:${String((q % 4) * 15).padStart(2, "0")}`;
      return `<option>${hhmm}</option>`;
    })
    .join("");
  day.onchange = () => {
    if (day.value && time.hidden) {
      const [h, m] = stockholmNow().slice(11).split(":").map(Number);
      const q = Math.min(95, Math.ceil((h * 60 + m) / 15));
      time.selectedIndex = q;
    }
    time.hidden = !day.value;
    update();
  };
  time.onchange = () => update();

  let mode = "transit";
  let kind = "reach";
  let request = 0;
  const points = { start: null, end: null };

  const setMode = (m) => {
    mode = m;
    for (const b of panel.querySelectorAll("[data-mode]")) b.classList.toggle("active", b.dataset.mode === m);
    depart.hidden = m !== "transit";
    update();
  };
  for (const b of panel.querySelectorAll("[data-mode]")) b.onclick = () => setMode(b.dataset.mode);
  const setKind = (k) => {
    kind = k;
    for (const b of panel.querySelectorAll("[data-kind]")) b.classList.toggle("active", b.dataset.kind === k);
    minutes.hidden = k !== "reach";
    if (k === "reach") unset("end");
    update();
  };
  for (const b of panel.querySelectorAll("[data-kind]")) b.onclick = () => setKind(b.dataset.kind);

  map.on("load", () => {
    const firstLabel = map.getStyle().layers.find((l) => l.type === "symbol")?.id;
    map.addSource("directions", { type: "geojson", data: empty() });
    map.addSource("reach", { type: "geojson", data: empty() });
    map.addLayer(
      { id: "reach", type: "fill", source: "reach", paint: { "fill-color": COLORS.route, "fill-opacity": 0.25 } },
      firstLabel,
    );
    map.addLayer(
      {
        id: "reach-outline",
        type: "line",
        source: "reach",
        paint: { "line-color": COLORS.route, "line-width": 1.5, "line-opacity": 0.8 },
      },
      firstLabel,
    );
    map.addLayer(
      {
        id: "directions-casing",
        type: "line",
        source: "directions",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#fff", "line-width": 9 },
      },
      firstLabel,
    );
    map.addLayer(
      {
        id: "directions",
        type: "line",
        source: "directions",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": ["match", ["get", "kind"], "walk", COLORS.walk, "ride", COLORS.ride, COLORS.route],
          "line-width": 5,
          "line-dasharray": ["match", ["get", "kind"], "walk", ["literal", [1, 1.5]], ["literal", [1, 0]]],
        },
      },
      firstLabel,
    );
  });

  const menu = new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: "menu" });
  let opened = 0;
  let pressed = 0;
  map.getCanvasContainer().addEventListener("pointerdown", () => (pressed = performance.now()));
  map.on("click", () => pressed > opened && menu.remove());
  map.on("contextmenu", (e) => {
    opened = performance.now();
    const actions = [
      ["Reach from here", "reach", "start"],
      ["Directions from here", "route", "start"],
      ["Directions to here", "route", "end"],
    ];
    const list = document.createElement("div");
    for (const [label, k, which] of actions) {
      const button = document.createElement("button");
      button.textContent = label;
      button.onclick = () => {
        menu.remove();
        if (kind !== k) setKind(k);
        place(which, e.lngLat);
      };
      list.append(button);
    }
    menu.setLngLat(e.lngLat).setDOMContent(list).addTo(map);
  });

  clear.onclick = () => {
    unset("start");
    unset("end");
    update();
  };

  function place(which, lngLat) {
    if (points[which]) points[which].setLngLat(lngLat);
    else {
      points[which] = new maplibregl.Marker({ color: MARKERS[which], draggable: true }).setLngLat(lngLat).addTo(map);
      points[which].on("dragend", update);
    }
    update();
  }

  function unset(which) {
    points[which]?.remove();
    points[which] = null;
  }

  function show(features, reach = []) {
    map.getSource("directions")?.setData({ type: "FeatureCollection", features });
    map.getSource("reach")?.setData({ type: "FeatureCollection", features: reach });
  }

  async function update() {
    const id = ++request;
    clear.hidden = !points.start && !points.end;
    journeys.replaceChildren();
    show([]);
    if (kind === "reach") return reach(id);
    if (!points.start || !points.end) {
      status.textContent = `${HINT} to set ${points.start ? "a destination" : "a start"}.`;
      return;
    }
    const [from, to] = [points.start, points.end].map((p) => {
      const { lat, lng } = p.getLngLat();
      return `${lat.toFixed(6)},${lng.toFixed(6)}`;
    });
    status.textContent = "Routing…";
    const url =
      mode === "transit"
        ? `/api/transit?from=${from}&to=${to}&at=${departure()}`
        : `/api/route?from=${from}&to=${to}&mode=${mode}&algo=alt`;
    let body;
    try {
      body = await get(url);
    } catch (err) {
      if (id === request) status.textContent = `No route: ${err.message}`;
      return;
    }
    if (id !== request) return;
    if (mode === "transit") showTransit(body.journeys);
    else {
      status.textContent = `${duration(body.duration_s)} · ${distance(body.distance_m)}`;
      show([{ type: "Feature", properties: { kind: "route" }, geometry: body.geometry }]);
    }
  }

  async function reach(id) {
    if (!points.start) {
      status.textContent = `${HINT} to set where to start.`;
      return;
    }
    const { lat, lng } = points.start.getLngLat();
    const budget = minutes.querySelector("select").value;
    // The drawing is a grid: coarser cells the farther the area reaches, so a
    // three-hour drive is a few hundred thousand cells, not tens of millions.
    const [perMinute, min, max] = { car: [6, 200, 1000], transit: [6, 200, 1000], bicycle: [2, 100, 400] }[mode] ?? [1, 100, 200];
    const cell = Math.min(max, Math.max(min, Math.round(budget * perMinute)));
    let url = `/api/isochrone?from=${lat.toFixed(6)},${lng.toFixed(6)}&mode=${mode}&minutes=${budget}`;
    url += `&geometry=polygon&cell_m=${cell}&reach_m=${cell}`;
    if (mode === "transit") url += `&at=${departure()}`;
    status.textContent = "Computing…";
    let body;
    try {
      body = await get(url);
    } catch (err) {
      if (id === request) status.textContent = `Nothing reachable: ${err.message}`;
      return;
    }
    if (id !== request) return;
    const label = MODES.find(([m]) => m === mode)[1].toLowerCase();
    status.textContent = `Reachable within ${duration(budget * 60)} by ${label}${mode === "transit" ? `, ${leaving()}` : ""}`;
    show([], body.features);
    fit(body.features);
  }

  // Zoom to the drawn area, clear of the panel: beside it on a wide screen,
  // below it on a phone, where it spans the top.
  function fit(features) {
    const bounds = new maplibregl.LngLatBounds();
    for (const f of features) for (const ring of rings(f.geometry)) for (const c of ring) bounds.extend(c);
    if (bounds.isEmpty()) return;
    const box = panel.getBoundingClientRect();
    const beside = box.right < map.getContainer().clientWidth / 2;
    const padding = { top: 40, right: 50, bottom: 40, left: 40 };
    if (beside) padding.left += box.right;
    else padding.top += Math.min(box.bottom, map.getContainer().clientHeight / 2);
    map.fitBounds(bounds, { padding, maxZoom: 16 });
  }

  function departure() {
    return day.value ? `${day.value}T${time.value}` : stockholmNow();
  }

  function leaving() {
    if (!day.value) return "leaving now";
    return `leaving ${day.selectedOptions[0].text} at ${time.value}`;
  }

  async function get(url) {
    const response = await fetch(url);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error ?? response.statusText);
    return body;
  }

  function showTransit(found) {
    if (!found.length) {
      status.textContent = "No journeys found.";
      return;
    }
    status.textContent = `${found.length} journey${found.length > 1 ? "s" : ""}, ${leaving()}`;
    found = [...found].sort((a, b) => a.arrival.localeCompare(b.arrival) || a.boardings - b.boardings);
    const select = (j, item) => {
      for (const li of journeys.children) li.classList.toggle("active", li === item);
      show(j.legs.map((leg) => ({ type: "Feature", properties: { kind: leg.type }, geometry: leg.geometry })));
    };
    for (const j of found) {
      const item = document.createElement("li");
      const rides = j.legs.filter((l) => l.type === "ride").map((l) => l.route_short_name || "?");
      item.innerHTML = `<b>${clock(j.departure)}–${clock(j.arrival)}</b> ${duration(j.duration_s)}<br>
        <span>${rides.length ? rides.map((r) => `<i>${escape(r)}</i>`).join(" › ") : "Walk"}</span>`;
      item.onclick = () => select(j, item);
      journeys.append(item);
    }
    select(found[0], journeys.firstChild);
  }

  setMode(mode);
  setKind(kind);
}

function rings(geometry) {
  if (geometry.type === "Polygon") return geometry.coordinates;
  if (geometry.type === "MultiPolygon") return geometry.coordinates.flat();
  return [];
}

function empty() {
  return { type: "FeatureCollection", features: [] };
}

// hitta takes local time in the timetable's zone.
function stockholmNow() {
  return new Date().toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" }).slice(0, 16).replace(" ", "T");
}

function addDays(date, n) {
  const d = new Date(`${date}T12:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function weekday(date) {
  return new Date(`${date}T12:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
}

function clock(rfc3339) {
  return rfc3339.slice(11, 16);
}

function duration(seconds) {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
}

function distance(metres) {
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

function escape(text) {
  return text.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
}
